import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react'
import { DateTime } from 'luxon'
import "./VisitForm.sass"

import {
  Button,
  Stack,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  IconButton,
  Autocomplete,
  TextField,
  Alert,
  Badge,
  styled,
  Dialog,
  DialogContent,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  LocalSeeOutlined as LocalSeeOutlinedIcon,
  QrCodeOutlined as QrCodeOutlinedIcon,
  DocumentScannerOutlined as DocumentScannerOutlinedIcon,
  InfoOutlined as InfoOutlinedIcon,
  EventOutlined as EventOutlinedIcon,
  AccessTimeOutlined as AccessTimeOutlinedIcon,
  Layers as LayersOutlinedIcon,
  Close as CloseIcon,
  ChevronRight,
  ChevronLeft,
  StickyNote2Outlined,
} from '@mui/icons-material'

import { useNavigate, useLocation } from 'react-router-dom'

import {
  setLoading,
  defaultSuccess,
  defaultCatch,
  setSnackbar,
} from '../../../state/actions'

import Field from '../Field/Field'
import { debounce, defaultFunction, getCompany, getVisitDestination } from '../../utilities/utilities'
import { connect } from 'react-redux'
import { api } from '../../../api/api'
import db, { findLatestVisitByIdentification, findLatestVisitByLicensePlate, findInvitationByQRCode, enqueueVisitPhoto } from '../../../db/db'
import { runPoll } from '../../../sync/SyncEngine'
import VisitAutoComplete from '../VisitAutoComplete/VisitAutoComplete'
import { DEFAULT_AUTOCOMPLETE, DEFAULT_BLACK_LIST_USER, MAX_LICENSE_PLATE_LENGTH, REGEX_LICENSE_PLATE } from '../../../variables'
import PhotoDialog from '../PhotoDialog/PhotoDialog'
import QRDialog from '../QRDialog/QRDialog'
import MRZDialog from '../MRZDialog/MRZDialog'
import IDDialog from '../IDDialog/IDDialog'
import IdentificationPhotoButton from '../IdentificationPhotoButton/IdentificationPhotoButton'
import BlackListDialog from '../BlackListDialog/BlackListDialog'
import InvitationNotesDialog from '../InvitationNotesDialog/InvitationNotesDialog'
import VisitHostAutoComplete from '../VisitHostAutoComplete/VisitHostAutoComplete'
import CheckoutDialog from '../CheckoutDialog/CheckoutDialog'
import { formatDateToISOShort } from '../../utilities/FormatDate'
import InfraredReader, { SCAN_SUCCESS_GUARD_MS } from '../InfraredReader/InfraredReader'
import HighlightOffOutlinedIcon from '../Svgs/HighlightOffOutlinedIcon'
import Building from '../Svgs/Building'
import { findActiveVisitByCardNumber } from '../../utilities/visitValidations'
import { isInvitationOutOfTime, getInvitationTimeStatus, isQrExpired, attachInvitationQrValidity } from '../../utilities/invitationValidations'

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#1D8099',
    color: '#1D8099',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      content: '""',
    },
  },
}))

// Timeout del fallback remoto (invitations/:code, visits/search). La réplica
// local ya respondió antes de llegar a la red, así que esta consulta solo corre
// cuando Dexie no tiene el dato; si el servidor tarda, no debe dejar el spinner
// colgado ni dejar el QRDialog abierto sin respuesta.
const REMOTE_FALLBACK_TIMEOUT_MS = 5000

function isOpenVisit(visit) {
  return !visit?.exit_time || `${visit.exit_time}`.trim() === ''
}

function isEntryTodayOrFuture(entry_time) {
  if (!entry_time) return false
  const parsed = new Date(entry_time)
  if (!isNaN(parsed.getTime())) {
    return DateTime.fromJSDate(parsed).startOf('day') >= DateTime.local().startOf('day')
  }
  const datePart = `${entry_time}`.split(' ')[0]
  const fromIso = DateTime.fromISO(datePart)
  return fromIso.isValid && fromIso.startOf('day') >= DateTime.local().startOf('day')
}

function VisitForm(props) {
  const {
    loading,
    isOnline,
    isCostaRica,
    isAFZ,
    enabledInfraRedReader,
    enabledMrzWebcam,
    building_id,
    ask_visit_comes_from,
    ask_visit_identification,
    ask_visit_laptop,
    ask_visit_card_number,
    ask_visit_license_plate,
    custom_visit_identification_label,
    enabled_visit_host,
    enabled_visit_types,
    enabled_visit_photo,
    enabled_visit_qr,
    enabled_identification_photo,
    save_identification_photo,
    enabled_local_ocr,
    enabled_black_list_by_name,
    setLoading,
    defaultSuccess,
    defaultCatch,
    setSnackbar,
    visitDestinations = [],
    visitTypes = [],
    companies = [],
    buildings = [],
    loadVisits = defaultFunction,
    exitConfirmation = defaultFunction,
    editConfirmation = defaultFunction,
  } = props

  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const DEFAULT_COMEFROM = isAFZ ? 'Personal' : ''
  const identificationLabel = custom_visit_identification_label || t('Identification')

  const [open, setOpen] = useState(true)
  const [isInvited, setIsInvited] = useState(false)
  const [identification, setIdentification] = useState('')
  const [first_name, setFirstName] = useState('')
  const [last_name_one, setLastNameOne] = useState('')
  const [last_name_two, setLastNameTwo] = useState('')
  const [comes_from, setComesFrom] = useState(DEFAULT_COMEFROM)
  const [visitDestination, setVisitDestination] = useState(DEFAULT_AUTOCOMPLETE)
  const [hasCardNumber, setHasCardNumber] = useState(false)
  const [has_laptop, setHasLaptop] = useState(false)
  const [card_number, setCardNumber] = useState('')
  const [cardNumberError, setCardNumberError] = useState('')
  const [isCheckingCardNumber, setIsCheckingCardNumber] = useState(false)
  const cardNumberRequestIdRef = useRef(0)
  const [laptop_serie, setLaptopSerie] = useState('')
  const [notes, setNotes] = useState('')
  const [invitationNotes, setInvitationNotes] = useState('')
  const [openQRDialog, setOpenQRDialog] = useState(false)
  const [openMRZDialog, setOpenMRZDialog] = useState(false)
  const [openPhotoDialog, setOpenPhotoDialog] = useState(false)
  const [photo, setPhoto] = useState('')
  const [openIDDialog, setOpenIDDialog] = useState(false)
  const [identificationPhoto, setIdentificationPhoto] = useState('')
  const [out_of_time, setOutOfTime] = useState(false)
  const [isAlreadyRegistered, setIsAlreadyRegistered] = useState(false)
  const [blackListUser, setBlackListUser] = useState(DEFAULT_BLACK_LIST_USER)
  const [blackListDialog, setOpenBlackListDialog] = useState(false)
  const [rejectReason, setRejectReason] = useState({})
  const [openInvitationNotesDialog, setOpenInvitationNotesDialog] = useState(false)
  const [noteIsAlreadyRead, setNoteIsAlreadyRead] = useState(false)
  const [hostName, setHostName] = useState('')
  const [showInvitationNotesButton, setShowInvitationNotesButton] = useState(false)
  const [visitHost, setVisitHost] = useState('')
  const [license_plate, setLicensePlate] = useState('')
  const [licensePlateError, setLicensePlateError] = useState('')
  const [type, setType] = useState(null)
  const [checkout, setCheckout] = useState(false)
  const [selectedItem, setSelectedItem] = useState({})
  const [qrResultModal, setQrResultModal] = useState({
    open: false,
    variant: 'error',
    title: '',
    description: '',
    details: [],
    buttonLabel: '',
    schedule: null,
    location: null,
    shouldReopenQR: false,
  })
  const qrScanProcessedRef = useRef(false)
  const qrScanReleaseTimeoutRef = useRef(null)
  const blackListOpenedByIdRef = useRef(false)
  const lastNameSearchKeyRef = useRef('')

  const loadingRef = useRef(loading);
  loadingRef.current = loading;

  const scannedNamesRef = useRef({ first_name: '', last_name_one: '', last_name_two: '' });

  useEffect(() => {
    setOpenInvitationNotesDialog(invitationNotes !== '')
    setShowInvitationNotesButton(invitationNotes !== '')
  }, [invitationNotes])

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = prev }
    }
  }, [open])

  const onChangeCardNumber = value => {
    setHasCardNumber(value === 'true')
    setCardNumber('')
    setCardNumberError('')
  }

  const onChangePortail = value => {
    setHasLaptop(value === 'true')
    setLaptopSerie('')
  }

  const onClosePhotoDialog = () => setOpenPhotoDialog(false)

  const cleanForm = useCallback((options = {}) => {
    const {
      cleanID = true,
      cleanOpen = true,
      cleanIdentificationPhoto = true
    } = options

    setIsInvited(false)
    scannedNamesRef.current = { first_name: '', last_name_one: '', last_name_two: '' }
    if (cleanOpen) setOpen(false)
    if (cleanID) setIdentification('')
    if (cleanIdentificationPhoto) setIdentificationPhoto('')
    setFirstName('')
    setLastNameOne('')
    setLastNameTwo('')
    setComesFrom(DEFAULT_COMEFROM)
    setVisitDestination(DEFAULT_AUTOCOMPLETE)
    setHasLaptop(false)
    setLaptopSerie('')
    setHasCardNumber(false)
    setCardNumber('')
    setCardNumberError('')
    setVisitHost('')
    setInvitationNotes('')
    setHostName('')
    setPhoto('')
    setNotes('')
    setLicensePlate('')
    setLicensePlateError('')
    setType(null)
    setCheckout(false)
    setSelectedItem({})
    qrScanProcessedRef.current = false
    if (qrScanReleaseTimeoutRef.current) {
      clearTimeout(qrScanReleaseTimeoutRef.current)
      qrScanReleaseTimeoutRef.current = null
    }
    blackListOpenedByIdRef.current = false
    lastNameSearchKeyRef.current = ''
  }, [DEFAULT_COMEFROM])


  const onCloseQRDialog = useCallback(() => {
    if (isAlreadyRegistered || out_of_time) {
      cleanForm()
    }
    setOpenQRDialog(false)
    setOutOfTime(false)
    setIsAlreadyRegistered(false)
  }, [setOpenQRDialog, setOutOfTime, setIsAlreadyRegistered, cleanForm, isAlreadyRegistered, out_of_time])

  const vigenciaModalOptions = useCallback((options = {}) => ({
    buttonLabel: options.buttonLabel || t('Capture another QR'),
    shouldReopenQR: options.shouldReopenQR !== false,
  }), [t])

  const openInvalidQRModal = useCallback((options = {}) => {
    const { buttonLabel, shouldReopenQR } = vigenciaModalOptions(options)
    setQrResultModal({
      open: true,
      variant: 'error',
      title: t('QR invalid title'),
      description: t('QR invalid description'),
      details: [],
      buttonLabel,
      schedule: null,
      location: null,
      shouldReopenQR,
    })
  }, [t, vigenciaModalOptions])

  // El lookup del QR falló por algo que no es "QR inválido" (red, 5xx, sesión,
  // excepción de Dexie). Sin este modal el QRDialog quedaba abierto con el QR
  // aún frente a la cámara y el escaneo se repetía en bucle sin avisar nada.
  const openQRLookupErrorModal = useCallback((options = {}) => {
    const { shouldReopenQR } = vigenciaModalOptions(options)
    setQrResultModal({
      open: true,
      variant: 'error',
      title: t('QR lookup error title'),
      description: t('QR lookup error description'),
      details: [],
      buttonLabel: options.buttonLabel || t('Scan again'),
      schedule: null,
      location: null,
      shouldReopenQR,
    })
  }, [t, vigenciaModalOptions])

  const getInvitationDisplayDate = useCallback((invitation = {}) => {
    const startDate = invitation.invitation_datetime || ''

    if (!startDate) return ''

    // Keep the invitation's own zone (e.g. -06:00) so the calendar day matches
    // creation, even if the browser is in another TZ (e.g. America/Caracas).
    const formattedDate = DateTime
      .fromISO(startDate, { setZone: true })
      .setLocale('es')
      .toFormat("d 'de' LLLL")
    return formattedDate
  }, [])

  /**
   * Visit schedule for QR modals: wall-clock times in the invitation's zone.
   * Do NOT use getTime()/removeTimezoneFromISO — stripping the offset and
   * re-parsing as local shifts the end when the browser TZ ≠ tenant TZ
   * (e.g. 8:00–20:00 -06:00 shown as 8:00 am – 10:00 pm in Caracas UTC−4).
   */
  const getInvitationVisitSchedule = useCallback((invitation = {}) => {
    const startRaw = invitation.invitation_datetime || ''
    if (!startRaw) return ''

    const start = DateTime.fromISO(startRaw, { setZone: true })
    if (!start.isValid) return ''

    let end = start.plus({ minutes: Number(invitation.duration) || 0 })
    if (invitation.invitation_exit_time) {
      const parsedEnd = DateTime.fromISO(invitation.invitation_exit_time, { setZone: true })
      if (parsedEnd.isValid) end = parsedEnd.setZone(start.zone)
    }

    const fmt = dt => dt.toFormat('h:mm a').toLowerCase()
    return `${fmt(start)} - ${fmt(end)}`
  }, [])

  const openEarlyQRModal = useCallback((invitation = {}, minutes = 0, options = {}) => {
    const scheduleDate = getInvitationDisplayDate(invitation)
    const scheduleTime = getInvitationVisitSchedule(invitation)
    const { buttonLabel, shouldReopenQR } = vigenciaModalOptions(options)

    setQrResultModal({
      open: true,
      variant: 'error',
      title: t('QR invalid title'),
      description: t('QR early description', { minutes }),
      details: invitation.id ? [t('Valid pre registration for')] : [],
      buttonLabel,
      schedule: {
        date: scheduleDate || ' - ',
        time: scheduleTime,
      },
      location: null,
      shouldReopenQR,
    })
  }, [getInvitationDisplayDate, getInvitationVisitSchedule, t, vigenciaModalOptions])

  const openExpiredQRModal = useCallback((options = {}) => {
    const { buttonLabel, shouldReopenQR } = vigenciaModalOptions(options)
    setQrResultModal({
      open: true,
      variant: 'error',
      title: t('QR invalid title'),
      description: t('QR expired description'),
      details: [],
      buttonLabel,
      schedule: null,
      location: null,
      shouldReopenQR,
    })
  }, [t, vigenciaModalOptions])

  const openWrongBuildingQRModal = useCallback((invitation = {}, options = {}) => {
    const destination = invitation.destination || {}
    const building = destination.buildings?.[0]
      || buildings.find(b => b.id === invitation.building_id)
      || {}
    const { buttonLabel, shouldReopenQR } = vigenciaModalOptions(options)

    setQrResultModal({
      open: true,
      variant: 'error',
      title: t('QR invalid in building title'),
      description: t('QR invalid in building description'),
      details: [],
      buttonLabel,
      schedule: null,
      location: {
        building: building.name || '-',
        destination: destination.name || '-',
      },
      shouldReopenQR,
    })
  }, [t, buildings, vigenciaModalOptions])

  const closeQRResultModal = useCallback(() => {
    setQrResultModal(prevState => ({ ...prevState, open: false }))
  }, [])

  const releaseQrScanGuard = useCallback(() => {
    if (qrScanReleaseTimeoutRef.current) {
      clearTimeout(qrScanReleaseTimeoutRef.current)
    }
    qrScanReleaseTimeoutRef.current = setTimeout(() => {
      qrScanProcessedRef.current = false
      qrScanReleaseTimeoutRef.current = null
    }, SCAN_SUCCESS_GUARD_MS)
  }, [])

  const openQRDialogForScan = useCallback(() => {
    qrScanProcessedRef.current = false
    if (qrScanReleaseTimeoutRef.current) {
      clearTimeout(qrScanReleaseTimeoutRef.current)
      qrScanReleaseTimeoutRef.current = null
    }
    setOpenQRDialog(true)
  }, [])

  const onQRResultModalAction = useCallback(() => {
    const shouldReopenQR = qrResultModal.shouldReopenQR
    closeQRResultModal()
    if (shouldReopenQR) {
      openQRDialogForScan()
    }
  }, [closeQRResultModal, openQRDialogForScan, qrResultModal.shouldReopenQR])


  const onCloseblackListDialog = () => {
    if (blackListUser?.id) {
      api.post('black_list/attempts', {
        black_list_id: blackListUser.id,
        reject_reason_id: rejectReason?.id,
      }).catch(error => console.error('[VisitForm] Error registrando intento de blacklist:', error))
    }
    setBlackListUser(DEFAULT_BLACK_LIST_USER)
    setRejectReason({})
    setOpenBlackListDialog(false)
  }

  const evalLicensePlate = useCallback((value = license_plate) => {
    const error = value && !REGEX_LICENSE_PLATE.test(value)
      ? t('invalid license plate')
      : ''

    setLicensePlateError(error)
    return error
  }, [license_plate, t])

  const setVisit = useCallback(visit => {
    const {
      identification = '',
      first_name = '',
      last_name_one = '',
      last_name_two = '',
      company_id,
      comes_from = '',
      laptop_serie = '',
      license_plate = '',
      has_laptop = false,
      card_number = ''
    } = visit;

    let {
      destination = DEFAULT_AUTOCOMPLETE
    } = visit;

    const safeString = (value) => (value != null ? value : '');

    const resolvedFirstName   = scannedNamesRef.current.first_name   || safeString(first_name);
    const resolvedLastNameOne = scannedNamesRef.current.last_name_one || safeString(last_name_one);
    const resolvedLastNameTwo = scannedNamesRef.current.last_name_two || safeString(last_name_two);

    setIdentification(identification)
    setFirstName(resolvedFirstName);
    setLastNameOne(resolvedLastNameOne);
    setLastNameTwo(resolvedLastNameTwo);
    setComesFrom(safeString(comes_from));
    if (ask_visit_license_plate) {
      const plate = safeString(license_plate)
      setLicensePlate(plate)
      evalLicensePlate(plate)
    }
    setLaptopSerie(safeString(laptop_serie));
    setCardNumber(safeString(card_number));

    if (destination.name === "") {
      let companyName = getCompany(company_id, companies).toLocaleLowerCase()
      destination = visitDestinations.find(d => d.name.toLocaleLowerCase() === companyName) || DEFAULT_AUTOCOMPLETE
    }
    setVisitDestination(destination)

    if (has_laptop && laptop_serie) {
      setHasLaptop(true)
      setLaptopSerie(laptop_serie)
    } else {
      setHasLaptop(false)
      setLaptopSerie('')
    }

    if (card_number) {
      setHasCardNumber(true)
      setCardNumber(card_number)
    } else {
      setHasCardNumber(false)
      setCardNumber('')
    }
  }, [ask_visit_license_plate, companies, evalLicensePlate, visitDestinations])

  const validateActiveCardNumber = useCallback(async () => {
    const normalizedCardNumber = (card_number || '').trim()
    const requestId = ++cardNumberRequestIdRef.current
    const isStale = () => requestId !== cardNumberRequestIdRef.current

    if (!ask_visit_card_number || !hasCardNumber || !normalizedCardNumber) {
      if (!isStale()) setCardNumberError('')
      return false
    }

    setIsCheckingCardNumber(true)
    try {
      const activeVisit = await findActiveVisitByCardNumber({
        cardNumber: normalizedCardNumber,
      })
      if (isStale()) return false
      const hasActiveVisit = !!activeVisit
      setCardNumberError(hasActiveVisit ? t('card_number_in_use') : '')
      return hasActiveVisit
    } catch (error) {
      console.error('[VisitForm] Error validating card number:', error)
      if (!isStale()) setCardNumberError('')
      return false
    } finally {
      if (!isStale()) setIsCheckingCardNumber(false)
    }
  }, [ask_visit_card_number, card_number, hasCardNumber, t])

  useEffect(() => {
    if (!ask_visit_card_number || !hasCardNumber || !card_number.trim()) {
      return
    }

    validateActiveCardNumber()
  }, [ask_visit_card_number, card_number, hasCardNumber, validateActiveCardNumber])

  const openBlackListMatch = useCallback((entry) => {
    if (!entry) return false
    setBlackListUser(entry)
    setRejectReason(entry.reject_reason || {})
    setOpenBlackListDialog(true)
    return true
  }, [])

  const onSearchBlackList = useCallback(async (value) => {
    if (!value) return false
    setLoading(true)
    try {
      const found = await db.black_lists
        .where('identification').equals(value)
        .first()
      if (found) {
        blackListOpenedByIdRef.current = true
        return openBlackListMatch(found)
      }
      return false
    } catch (error) {
      console.error('[VisitForm] Error buscando blacklist localmente:', error)
      return false
    }
  }, [openBlackListMatch, setLoading])

  const onSearchBlackListByName = useCallback(async (firstName, lastName) => {
    if (!enabled_black_list_by_name) return false
    if (blackListOpenedByIdRef.current) return false
    const first = (firstName || '').trim()
    const last = (lastName || '').trim()
    if (!first || !last) return false

    const searchKey = `${first.toLowerCase()}|${last.toLowerCase()}`
    if (lastNameSearchKeyRef.current === searchKey) return false
    lastNameSearchKeyRef.current = searchKey

    setLoading(true)
    try {
      const found = await db.black_lists
        .filter(row =>
          (row.first_name || '').trim().toLowerCase() === first.toLowerCase() &&
          (row.last_name_one || '').trim().toLowerCase() === last.toLowerCase()
        )
        .first()
      // Solo Dexie: `db.black_lists` es una réplica completa y el filtro de
      // arriba equivale al de `black_list/search_by_name`. Consultar el
      // endpoint además creaba un BlackListAttempt en el servidor que se
      // duplicaba con el que registra `onCloseblackListDialog`.
      return found ? openBlackListMatch(found) : false
    } catch (error) {
      console.error('[VisitForm] Error buscando blacklist por nombre:', error)
      return false
    } finally {
      setLoading(false)
    }
  }, [enabled_black_list_by_name, openBlackListMatch, setLoading])

  const onSearchByCRCI = useCallback(async (value) => {
    // En web no hay padrón local: sin red no hay nada que consultar. Se evita
    // la petición que fallaría y pasaría por `defaultCatch`.
    if (!window.electronAPI && !isOnline) return null
    setLoading(true)
    try {
      const response = window.electronAPI ?
        await window.electronAPI.onSearchCRPadron(value) :
        await api.get(`cr/person/${value}`)


      defaultSuccess(response)
      const { data } = response
      console.log(response)
      if (!data?.person?.id) return null

      const {
        id,
        cedula: identification,
        nombre: first_name,
        apellido_1: last_name_one,
        apellido_2: last_name_two,
      } = data.person

      return {
        id,
        identification,
        first_name,
        last_name_one,
        last_name_two
      }
    } catch (error) {
      defaultCatch(error, navigate)
    }
  }, [defaultCatch, defaultSuccess, isOnline, navigate, setLoading])

  const getVisitHost = useCallback((user) => {
    if (!user) return ''
    const { id, name } = user

    return {
      id,
      label: name,
    }
  }, [])

  const openCheckoutDialog = useCallback((item) => {
    setSelectedItem(item)
    setCheckout(true)
  }, [])

  const evaluateIsAlreadyCheckin = useCallback((visit = {}) => {
    const { entry_time = '' } = visit

    if (
      isOpenVisit(visit) &&
      entry_time &&
      isEntryTodayOrFuture(entry_time)
    ) {
      openCheckoutDialog(visit)
      return true
    }

    return false
  }, [openCheckoutDialog])

  const onSearchByInvitations = useCallback(async (value) => {
    setLoading(true)
    try {
      const todayISO = formatDateToISOShort(new Date())
      const found = await db.invitations
        .filter(inv => inv.identification === value && inv.invitation_date === todayISO)
        .first()
      if (!found) return null
      const invitation = attachInvitationQrValidity(found, companies)
      if (isInvitationOutOfTime(invitation)) return null
      return invitation
    } catch (error) {
      console.error('[VisitForm] Error buscando invitación localmente:', error)
      return null
    }
  }, [setLoading, companies])

  /**
   * Local primero: `db.visits` (ventana de 90 días, mantenida por SyncEngine)
   * ya incluye las visitas de hoy, incluidas las `pending` creadas en este
   * kiosko, que es lo único que necesita la detección de checkout.
   *
   * `visits/search` es solo un fallback para historial más viejo que la
   * ventana local, y únicamente cuando no hay NADA local con qué autocompletar:
   * ni visita ni invitación (`skipRemote`). Si ya hay invitación, `setForm` la
   * usa para autocompletar y la visita solo serviría para el checkout, que
   * Dexie ya cubre.
   */
  const onSearchByVisits = useCallback(async (value, { skipRemote = false } = {}) => {
    setLoading(true)
    let localVisit = null
    try {
      localVisit = await findLatestVisitByIdentification(value)

      if (localVisit || skipRemote || !isOnline) return localVisit

      const response = await api.get(
        `visits/search?identification=${encodeURIComponent(value)}`,
        { timeout: REMOTE_FALLBACK_TIMEOUT_MS }
      )
      defaultSuccess(response)
      const { data } = response
      return data?.visit?.id ? data.visit : null
    } catch (error) {
      if (isOnline) defaultCatch(error, navigate)
      return localVisit
    }
  }, [defaultCatch, defaultSuccess, navigate, setLoading, isOnline])

  const setForm = useCallback((invitationData, visitData) => {
    const hasVisit = !!(visitData?.id || visitData?.uuid)
    if (hasVisit && evaluateIsAlreadyCheckin(visitData)) return
    if (invitationData?.id) {
      setVisit(invitationData)
      setInvitationNotes(invitationData.notes || '')
      setHostName(invitationData.user?.name || '')
      setVisitHost(getVisitHost(invitationData.user))
      setIsInvited(true)
    } else if (hasVisit) {
      setVisit(visitData)
      setIsInvited(false)
    }
  }, [evaluateIsAlreadyCheckin, setVisit, getVisitHost])

  const onSearch = useCallback(async (value, { invitationData, fromQR = false } = {}) => {
    if (!value) return cleanForm({ cleanID: false, cleanOpen: false, cleanIdentificationPhoto: false })
    try {
      if (!fromQR) await onSearchBlackList(value)
      if (!fromQR && !invitationData?.id) invitationData = await onSearchByInvitations(value)
      let visitData = await onSearchByVisits(value, { skipRemote: !!invitationData?.id })

      if (!fromQR && isCostaRica && !(invitationData?.id || visitData?.id || visitData?.uuid)) {
        visitData = await onSearchByCRCI(value)
        if (visitData) setVisit(visitData)
      } else setForm(invitationData, visitData)
    } finally {
      setLoading(false)
    }
  }, [cleanForm, isCostaRica, onSearchBlackList, onSearchByCRCI, onSearchByInvitations, onSearchByVisits, setForm, setVisit, setLoading])

  const hydrateFromInvitation = useCallback(async (rawInvitation, {
    fromQR = false,
    fromPreRegister = false,
    qrCode = '',
  } = {}) => {
    if (!rawInvitation) return
    setLoading(true)
    const modalOptions = fromPreRegister
      ? { shouldReopenQR: false, buttonLabel: t('close') }
      : {}
    try {
      const invitation = attachInvitationQrValidity(rawInvitation, companies)
      const timeStatus = getInvitationTimeStatus(invitation)
      const invitationExitTime = (() => {
        if (!invitation) return null
        if (invitation.invitation_exit_time) return invitation.invitation_exit_time
        if (!invitation.invitation_datetime) return null
        const start = DateTime.fromISO(invitation.invitation_datetime)
        if (!start.isValid) return null
        return start.plus({ minutes: invitation.duration || 0 }).toISO()
      })()

      const stampedInvitation = invitation
        ? { ...invitation, invitation_exit_time: invitationExitTime }
        : null
      const invitationBuildingId = stampedInvitation?.destination?.buildings?.[0]?.id
        ?? stampedInvitation?.building_id
      const isDifferentBuilding = !!invitationBuildingId && invitationBuildingId !== building_id
      if (invitation && isQrExpired(invitation)) {
        onCloseQRDialog()
        openExpiredQRModal(modalOptions)
        return
      }

      if (timeStatus.outOfTime && timeStatus.isOutOfPrevTime) {
        onCloseQRDialog()
        openEarlyQRModal(
          stampedInvitation || invitation || {},
          invitation?.allowed_invitation_qr_validity_prev_time || 0,
          modalOptions
        )
        return
      }

      if (stampedInvitation && isDifferentBuilding) {
        onCloseQRDialog()
        openWrongBuildingQRModal(stampedInvitation, modalOptions)
        return
      }

      setOutOfTime(timeStatus.outOfTime || false)
      if (!timeStatus.outOfTime) onCloseQRDialog()

      const invitationData = stampedInvitation?.id ? stampedInvitation : null
      const invitationWithIdentification = (!ask_visit_identification && invitationData?.id)
        ? {
            ...invitationData,
            identification: qrCode || invitationData.identification || invitationData.unique_identificator || '',
          }
        : invitationData
      const searchIdentification = ask_visit_identification
        ? (invitationWithIdentification?.identification || qrCode || invitationWithIdentification?.unique_identificator)
        : (qrCode || invitationWithIdentification?.identification || invitationWithIdentification?.unique_identificator)

      if (!searchIdentification) {
        if (invitationWithIdentification) setForm(invitationWithIdentification, null)
        return
      }

      await onSearch(searchIdentification, { invitationData: invitationWithIdentification, fromQR })
    } catch (error) {
      console.error('[VisitForm] Error hidratando invitación:', error)
      onCloseQRDialog()
      openQRLookupErrorModal(modalOptions)
    } finally {
      setLoading(false)
    }
  }, [
    ask_visit_identification,
    building_id,
    companies,
    onCloseQRDialog,
    onSearch,
    openEarlyQRModal,
    openExpiredQRModal,
    openQRLookupErrorModal,
    openWrongBuildingQRModal,
    setForm,
    setLoading,
    t,
  ])

  const fromPreRegisterRef = useRef(location.state?.fromPreRegister)
  const isFromPreRegisterRef = useRef(Boolean(location.state?.fromPreRegister))
  useEffect(() => {
    const invitation = fromPreRegisterRef.current
    if (!invitation) return
    fromPreRegisterRef.current = null
    navigate('.', { replace: true, state: {} })
    hydrateFromInvitation(invitation, { fromPreRegister: true })
  }, [hydrateFromInvitation, navigate])

  const normalizeInvitationResponse = useCallback((response) => {
    const data = response?.data || {}
    const invitation = data.success && data.invitation && typeof data.invitation === 'object'
      ? data.invitation
      : null

    const building = data.building && typeof data.building === 'object'
      ? data.building
      : null
    const responseDestination = data.destination && typeof data.destination === 'object'
      ? data.destination
      : null
    const expiredInvitation = !invitation?.id && data.out_of_time
      ? {
          destination: responseDestination || {},
          invitation_datetime: data.invitation_datetime,
          invitation_exit_time: data.invitation_exit_time,
          allowed_invitation_qr_validity_prev_time: data.allowed_invitation_qr_validity_prev_time,
          allowed_invitation_qr_validity_post_time: data.allowed_invitation_qr_validity_post_time,
          out_of_time: data.out_of_time,
          is_out_of_prev_time: data.is_out_of_prev_time,
          is_out_of_post_time: data.is_out_of_post_time,
        }
      : null
    const normalizedInvitation = invitation?.id ? invitation : expiredInvitation

    if (!normalizedInvitation) return null

    const destination = normalizedInvitation.destination && typeof normalizedInvitation.destination === 'object'
      ? normalizedInvitation.destination
      : responseDestination && typeof responseDestination === 'object'
        ? responseDestination
        : {}
    const destinationBuilding = building || destination.building || null
    const hasDestinationBuildings = Array.isArray(destination.buildings) && destination.buildings.length > 0

    return {
      ...normalizedInvitation,
      destination: {
        ...destination,
        buildings: hasDestinationBuildings
          ? destination.buildings
          : destinationBuilding ? [destinationBuilding] : destination.buildings,
      },
      out_of_time: data.out_of_time,
      is_out_of_prev_time: data.is_out_of_prev_time,
      is_out_of_post_time: data.is_out_of_post_time,
    }
  }, [])

  const findInvitationByQR = useCallback(async (value) => {
    const localInvitation = await findInvitationByQRCode(value)
    if (localInvitation || !isOnline) return localInvitation

    const response = await api.get(
      `invitations/${encodeURIComponent(value)}`,
      { timeout: REMOTE_FALLBACK_TIMEOUT_MS }
    )
    defaultSuccess(response)
    return normalizeInvitationResponse(response)
  }, [defaultSuccess, isOnline, normalizeInvitationResponse])

  const onSearchByQRInvitations = useCallback(async (value) => {
    if (!value) return
    setLoading(true)
    try {
      const rawInvitation = await findInvitationByQR(value)
      if (!rawInvitation) {
        onCloseQRDialog()
        openInvalidQRModal()
        return
      }
      await hydrateFromInvitation(rawInvitation, { fromQR: true, qrCode: value })
    } catch (error) {
      console.error('[VisitForm] Error buscando invitación por QR:', error)
      // Cerrar el diálogo y avisar: si no, el QR sigue frente a la cámara y
      // `QrReader` re-dispara el escaneo cuando `releaseQrScanGuard` rearma el
      // guard, repitiendo el spinner indefinidamente sin ningún mensaje.
      defaultCatch(error, navigate)
      onCloseQRDialog()
      openQRLookupErrorModal()
    } finally {
      setLoading(false)
      releaseQrScanGuard()
    }
  }, [defaultCatch, findInvitationByQR, hydrateFromInvitation, navigate, onCloseQRDialog, openInvalidQRModal, openQRLookupErrorModal, releaseQrScanGuard, setLoading])

  const onSearchByLicensePlate = useCallback(async (value) => {
    setLoading(true)
    try {
      const found = await db.invitations
        .filter(inv => inv.license_plate === value)
        .first()
      if (found) {
        const invitation = attachInvitationQrValidity(found, companies)
        if (isInvitationOutOfTime(invitation)) return
        if (invitation.identification) await onSearchBlackList(invitation.identification)
        setForm(invitation, null)
        return
      }

      const visit = await findLatestVisitByLicensePlate(value)
      if (!visit) return
      if (visit.identification) await onSearchBlackList(visit.identification)
      setForm(null, visit)
    } catch (error) {
      console.error('[VisitForm] Error buscando por placa localmente:', error)
    } finally {
      setLoading(false)
    }
  }, [setLoading, onSearchBlackList, setForm, companies])

  const onEfficientSearch = useMemo(
    () => debounce((nextValue) => onSearch(nextValue), 1000),
    [onSearch]
  );

  // Escribir la placa recrea `setVisit` (vía `evalLicensePlate`) y, con él,
  // `onSearchByLicensePlate`. Si el debounce dependiera de esa identidad, el
  // efecto de limpieza cancelaría el timer en cada tecla y la búsqueda no
  // llegaría a correr. El ref mantiene el timer y llama a la versión vigente.
  const onSearchByLicensePlateRef = useRef(onSearchByLicensePlate)
  onSearchByLicensePlateRef.current = onSearchByLicensePlate

  const onEfficientSearchByPlate = useMemo(
    () => debounce((nextValue) => onSearchByLicensePlateRef.current(nextValue), 1000),
    []
  )

  const onEfficientSearchByName = useMemo(
    () => debounce((first, last) => onSearchBlackListByName(first, last), 1000),
    [onSearchBlackListByName]
  )

  useEffect(() => {
    return () => {
      onEfficientSearch.cancel();
    };
  }, [onEfficientSearch]);

  useEffect(() => {
    return () => { onEfficientSearchByPlate.cancel() }
  }, [onEfficientSearchByPlate])

  useEffect(() => {
    return () => { onEfficientSearchByName.cancel() }
  }, [onEfficientSearchByName])

  useEffect(() => {
    if (!enabled_black_list_by_name) return
    if (!first_name.trim() || !last_name_one.trim()) return
    onEfficientSearchByName(first_name, last_name_one)
  }, [enabled_black_list_by_name, first_name, last_name_one, onEfficientSearchByName])

  useEffect(() => {
    onEfficientSearch.cancel();
  }, [building_id, onEfficientSearch]);

  useEffect(() => {
    return () => {
      if (qrScanReleaseTimeoutRef.current) {
        clearTimeout(qrScanReleaseTimeoutRef.current)
      }
    }
  }, [])

  const onChangeIdentification = value => {
    onEfficientSearchByPlate.cancel()
    cleanForm({ cleanID: true, cleanOpen: false, cleanIdentificationPhoto: false })
    onCloseQRDialog()
    setIdentification(value)
    onEfficientSearch(value)
  }

  const onChangeUniqueIdentificator = useCallback(value => {
    if (qrScanProcessedRef.current) return
    onEfficientSearchByPlate.cancel()
    cleanForm({ cleanID: true, cleanOpen: false })
    qrScanProcessedRef.current = true
    if (!ask_visit_identification) {
      setIdentification(value || '')
    }
    onSearchByQRInvitations(value)
  }, [ask_visit_identification, cleanForm, onEfficientSearchByPlate, onSearchByQRInvitations])

  const onInvalidQR = useCallback(() => {
    if (qrScanProcessedRef.current) return
    qrScanProcessedRef.current = true
    onCloseQRDialog()
    openInvalidQRModal()
    releaseQrScanGuard()
  }, [onCloseQRDialog, openInvalidQRModal, releaseQrScanGuard])

  const onSaveVisit = useCallback(async () => {
    const uuid = crypto.randomUUID()
    const destination = visitDestinations.find(d => d.id === visitDestination.id) || visitDestination

    const visit = {
      uuid,
      id: null,
      syncStatus: 'pending',
      building_id,
      entry_time: new Date().toISOString(),
      identification,
      first_name,
      last_name_one,
      last_name_two,
      destination_id: visitDestination.id,
      destination,
      comes_from,
      card_number,
      notes,
      has_laptop,
      laptop_serie,
      license_plate,
      exit_time: null,
      visit_type_id: (enabled_visit_types && type) ? type.id : null,
      visit_type: (enabled_visit_types && type) ? type : null,
      visit_host_id: (enabled_visit_host && visitHost?.id) ? visitHost.id : null,
    }

    if (isOnline) {
      try {
        setLoading(true)
        const response = await api.post('/visit', {
          visit: {
            uuid,
            identification,
            first_name,
            last_name_one,
            last_name_two,
            destination_id: visitDestination.id,
            comes_from,
            card_number,
            notes,
            has_laptop,
            laptop_serie,
            license_plate,
            ...(enabled_visit_types && type ? { visit_type_id: type.id } : {}),
            ...(enabled_visit_host && visitHost?.id ? { visit_host_id: visitHost.id } : {}),
          },
        })
        defaultSuccess(response)
        const { data } = response

        if (data?.success) {
          const createdVisit = {
            ...visit,
            ...data.visit,
            uuid: data.visit?.uuid || uuid,
            syncStatus: 'synced',
            syncError: null,
            pushAttempts: 0,
          }
          await db.visits.put(createdVisit)
          window.dispatchEvent(new CustomEvent('lobby:visit-pending'))
          return createdVisit
        }

        setSnackbar({
          open: true,
          message: data?.errors?.[0] || t('Error saving visit'),
          severity: 'error',
          autoHideDuration: 2000,
          vertical: 'top',
          horizontal: 'right',
        })
        return null
      } catch (error) {
        console.warn('[VisitForm] Error creando visita online, se encola para reintento:', error)
      } finally {
        setLoading(false)
      }
    }

    try {
      setLoading(true)
      await db.visits.put(visit)
      window.dispatchEvent(new CustomEvent('lobby:visit-pending'))
      return visit
    } catch (error) {
      console.error('[VisitForm] Error guardando visita localmente:', error)
      setSnackbar({
        open: true,
        message: t('Error saving visit'),
        severity: 'error',
        autoHideDuration: 2000,
        vertical: 'top',
        horizontal: 'right',
      })
      return null
    }
  }, [card_number, comes_from, defaultSuccess, visitDestination, visitDestinations, enabled_visit_host, enabled_visit_types, first_name, has_laptop, laptop_serie, identification, isOnline, last_name_one, last_name_two, license_plate, notes, setLoading, setSnackbar, type, visitHost, building_id, t])

  const onSave = useCallback(async () => {
    setLoading(true)
    const visit = await onSaveVisit()
    if (!visit) {
      setLoading(false)
      return
    }
    if (enabled_visit_photo && photo) await enqueueVisitPhoto(visit.uuid, 'visit', photo)
    if (save_identification_photo && identificationPhoto) await enqueueVisitPhoto(visit.uuid, 'credential', identificationPhoto)

    if (isOnline) {
      // `runPoll`, no `runSync`: sube la visita y baja lo de hoy sin la
      // ventana completa de invitaciones (ver docs/local-first.md §5).
      runPoll().catch(console.error)
    }

    setSnackbar({
      open: true,
      message: isFromPreRegisterRef.current
        ? t('Guest registered successfully')
        : t('Successfully registered visitor'),
      severity: 'success',
      autoHideDuration: 2000,
      vertical: 'top',
      horizontal: 'right',
    })
    isFromPreRegisterRef.current = false
    cleanForm({ cleanID: true, cleanOpen: false })
    setShowInvitationNotesButton(false)
    setLoading(false)
    loadVisits()
  }, [cleanForm, enabled_visit_photo, identificationPhoto, isOnline, loadVisits, onSaveVisit, photo, setLoading, setShowInvitationNotesButton, setSnackbar, save_identification_photo, t])

  const isEnableSave = useCallback(() => {
    return (
      (!enabled_visit_types || type) &&
      first_name &&
      last_name_one &&
      visitDestination.id !== -1 &&
      (!ask_visit_comes_from || comes_from) &&
      (!ask_visit_card_number || !hasCardNumber || card_number) &&
      (!ask_visit_laptop || !has_laptop || laptop_serie) &&
      (!ask_visit_license_plate || (license_plate && REGEX_LICENSE_PLATE.test(license_plate))) &&
      !licensePlateError &&
      !cardNumberError &&
      !isCheckingCardNumber
    )
  }, [ask_visit_card_number, ask_visit_comes_from, ask_visit_laptop, ask_visit_license_plate, card_number, cardNumberError, comes_from, visitDestination, enabled_visit_types, first_name, hasCardNumber, has_laptop, isCheckingCardNumber, laptop_serie, last_name_one, licensePlateError, license_plate, type])

  const onChangeVisitDestination = (_, visitDestination) => setVisitDestination(visitDestination || DEFAULT_AUTOCOMPLETE)

  const onChangeType = (_, type) => {
    setType(type || null)
  }

  const toggleDrawer = useCallback((event) => {
    event.stopPropagation()
    setOpen(prevOpen => !prevOpen)
  }, [])

  const onChangDataByID = useCallback((data) => {
    onEfficientSearchByPlate.cancel()
    const {
      identification = '',
      first_name = '',
      last_name = '',
      last_name_two = '',
    } = data

    scannedNamesRef.current = {
      first_name,
      last_name_one: last_name,
      last_name_two,
    }

    onEfficientSearch(identification)
    setIdentification(identification)
    setFirstName(first_name)
    setLastNameOne(last_name)
    setLastNameTwo(last_name_two)
  }, [onEfficientSearch, onEfficientSearchByPlate]);

  const onCloseCheckoutDialog = () => {
    setCheckout(false)
    cleanForm({ cleanID: true, cleanOpen: false })
  }

  const onExitCheckoutDialog = () => {
    setCheckout(false)
    cleanForm({ cleanID: true, cleanOpen: false })
    exitConfirmation(selectedItem)
  }

  const onEditCheckoutDialog = () => {
    setCheckout(false)
    cleanForm({ cleanID: true, cleanOpen: false })
    editConfirmation(selectedItem)
  }

  const getInfraReader = useCallback(() => {
    if (enabledInfraRedReader) return (
      <InfraredReader
        onChangDataByID={onChangDataByID}
        onChangeUniqueIdentificator={onChangeUniqueIdentificator}
      />
    )
  }, [enabledInfraRedReader, onChangDataByID, onChangeUniqueIdentificator])

  const onChangeLicensePlate = useCallback(event => {
    const value = event.target.value || ''
    if (value.length > MAX_LICENSE_PLATE_LENGTH) return
    const upperValue = value.toUpperCase()
    setLicensePlate(upperValue)
    setLicensePlateError('')
    if (!identification && upperValue) {
      onEfficientSearchByPlate(upperValue)
    } else {
      onEfficientSearchByPlate.cancel()
    }
  }, [identification, onEfficientSearchByPlate])

  return (
    <div className={`VisitForm ${open ? 'open' : ''}`}>
      {getInfraReader()}
      <div className='form-wrapper' onClick={() => setOpen(false)}></div>
      <div className='form' onClick={() => setOpen(true)}>
        {isInvited &&
          <Alert
            className={`pre-register-alert ${open ? '' : 'hide'}`}
            icon={<CheckCircleOutlineIcon fontSize="inherit" />}
            severity="success"
          >
            {t('Pre-registered visit as a guest')}
          </Alert>
        }
        <Stack gap={3} className='form-inner'>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <h1>{open ? t('visitor registration') : t('Registration')}</h1>
            <Stack direction="row" gap={1} alignItems="center">
              {showInvitationNotesButton &&
                open &&
                <StyledBadge
                  variant="dot"
                  overlap="circular"
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  invisible={noteIsAlreadyRead}
                >
                  <IconButton
                    variant="outlined"
                    size='small'
                    onClick={() => setOpenInvitationNotesDialog(true)}
                    sx={{ borderRadius: '8px', border: '1px solid #6C85D3', color: '#6C85D3' }}
                  >
                    <StickyNote2Outlined />
                  </IconButton>
                </StyledBadge>
              }
              {(enabled_identification_photo || save_identification_photo) &&
                open &&
                <IdentificationPhotoButton
                  onClick={() => setOpenIDDialog(true)}
                  identificationPhoto={identificationPhoto}
                  saveIdentificationPhoto={save_identification_photo}
                />
              }
            </Stack>
          </Stack>
          {ask_visit_identification &&
            <Field
              label={identificationLabel}
              value={identification}
              onChange={e => onChangeIdentification(e.target.value)}
              direction={open ? 'row' : 'column'}
            >
              {enabled_visit_qr &&
                <Button
                  sx={{
                    padding: '7px',
                    minWidth: 'auto',
                    display: open ? '' : 'none',
                    borderRadius: '8px',
                    border: '1px solid'
                  }}
                  variant="outlined"
                  color="primary"
                  size='large'
                  aria-label="qr"
                  onClick={openQRDialogForScan}
                >
                  <QrCodeOutlinedIcon />
                </Button>
              }
              {enabledMrzWebcam &&
                <Button
                  sx={{
                    padding: '7px',
                    minWidth: 'auto',
                    display: open ? '' : 'none',
                    borderRadius: '8px',
                    border: '1px solid'
                  }}
                  variant="outlined"
                  color="primary"
                  size='large'
                  aria-label={t('Scan MRZ')}
                  onClick={() => setOpenMRZDialog(true)}
                >
                  <DocumentScannerOutlinedIcon />
                </Button>
              }
            </Field>
          }
          {enabled_visit_types &&
            <Stack
              className='Field'
              direction={open ? 'row' : 'column'}
              alignItems="center"
              justifyContent="space-between"
              gap={2}
            >
              <label>{t('type')}</label>
              <Autocomplete
                size='small'
                fullWidth
                options={visitTypes}
                getOptionLabel={option => option.name}
                onChange={onChangeType}
                value={type || null}
                renderOption={(props, option) => {
                  const { key, ...restProps } = props
                  return <li key={key} {...restProps}>{option.name}</li>
                }}
                renderInput={(params) => {
                  const { key, ...restParams } = params;
                  return <TextField
                    {...restParams}
                    label={t('write')}
                    variant="filled"
                  />
                }
                }
              />
            </Stack>
          }
          <Field
            label={t('name')}
            value={first_name}
            onChange={e => setFirstName(e.target.value)}
            direction={open ? 'row' : 'column'}
          >
            {!ask_visit_identification && enabled_visit_qr &&
              <Button
                sx={{
                  padding: '7px',
                  minWidth: 'auto',
                  display: open ? '' : 'none',
                  borderRadius: '8px',
                  border: '1px solid'
                }}
                variant="outlined"
                color="primary"
                size='large'
                aria-label="qr"
                onClick={openQRDialogForScan}
              >
                <QrCodeOutlinedIcon />
              </Button>
            }
            {!ask_visit_identification && enabledMrzWebcam &&
              <Button
                sx={{
                  padding: '7px',
                  minWidth: 'auto',
                  display: open ? '' : 'none',
                  borderRadius: '8px',
                  border: '1px solid'
                }}
                variant="outlined"
                color="primary"
                size='large'
                aria-label={t('Scan MRZ')}
                onClick={() => setOpenMRZDialog(true)}
              >
                <DocumentScannerOutlinedIcon />
              </Button>
            }
          </Field>
          <Field
            label={`${t('last name')} 1`}
            value={last_name_one}
            onChange={e => setLastNameOne(e.target.value)}
            direction={open ? 'row' : 'column'}
          />
          <Field
            label={`${t('last name')} 2`}
            value={last_name_two}
            onChange={e => setLastNameTwo(e.target.value)}
            direction={open ? 'row' : 'column'}
          />
          {enabled_visit_host &&
            <VisitHostAutoComplete
              label={t('host')}
              value={visitHost}
              setValue={setVisitHost}
              direction={open ? 'row' : 'column'}
            />
          }
          {
            ask_visit_comes_from &&
            <VisitAutoComplete
              label={t('comes from')}
              value={comes_from}
              setValue={setComesFrom}
              direction={open ? 'row' : 'column'}
            />
          }
          <Stack
            className='Field'
            gap={2}
            direction={open ? 'row' : 'column'}
            justifyContent="space-between"
            alignItems="center"
          >
            <label>{t('go to')}</label>
            <Autocomplete
              size='small'
              fullWidth
              options={getVisitDestination(visitDestinations)}
              getOptionLabel={option => option.name}
              onChange={onChangeVisitDestination}
              value={visitDestination || null}
              renderOption={(props, option) => {
                const { key, ...restProps } = props
                return <li key={key} {...restProps}>{option.name}</li>
              }}
              renderInput={(params) => {
                const { key, ...restParams } = params;
                return <TextField
                  {...restParams}
                  label={t('write')}
                  variant="filled"
                />
              }}
            />
          </Stack>
          {ask_visit_card_number &&
            <Stack
              className='Field'
              gap={2}
              direction={open ? 'row' : 'column'}
              alignItems="center"
            >
              <label>{t('Card')}</label>
              <Stack
                gap={2}
                direction="column"
              >
                <FormControl>
                  <RadioGroup
                    row
                    name="card"
                    value={hasCardNumber}
                    onChange={(_, value) => onChangeCardNumber(value)}
                  >
                    <FormControlLabel
                      value={true}
                      control={<Radio />}
                      label={t('Yes')}
                      disabled={!open}
                    />
                    <FormControlLabel
                      value={false}
                      control={<Radio />}
                      label={t('No')}
                      disabled={!open}
                    />
                  </RadioGroup>
                </FormControl>
                {hasCardNumber &&
                  <Field
                    fullWidth
                    label={t('Card number')}
                    value={card_number}
                    onChange={e => {
                      setCardNumber(e.target.value)
                      setCardNumberError('')
                    }}
                    onBlur={validateActiveCardNumber}
                    error={!!cardNumberError}
                    helperText={cardNumberError}
                    direction={open ? 'row' : 'column'}
                    alignItems='start'
                  />
                }
              </Stack>
            </Stack>
          }
          {ask_visit_laptop &&
            <Stack
              className='Field'
              gap={2}
              direction={open ? 'row' : 'column'}
              alignItems="center"
            >
              <label>{t('Laptop')}</label>
              <Stack
                gap={2}
                direction="column"
              >
                <FormControl>
                  <RadioGroup
                    row
                    aria-labelledby="laptop-radio"
                    name="laptop"
                    value={has_laptop}
                    onChange={(_, value) => onChangePortail(value)}
                  >
                    <FormControlLabel
                      value={true}
                      control={<Radio />}
                      label={t('Yes')}
                      disabled={!open}
                    />
                    <FormControlLabel
                      value={false}
                      control={<Radio />}
                      label={t('No')}
                      disabled={!open}
                    />
                  </RadioGroup>
                </FormControl>
                {has_laptop &&
                  <Field
                    fullWidth
                    label={t('No. Serie')}
                    value={laptop_serie}
                    onChange={e => setLaptopSerie(e.target.value)}
                  />
                }
              </Stack>
            </Stack>
          }
          {enabled_visit_photo &&
            <Stack
              className='Field'
              gap={2}
              direction={open ? 'row' : 'column'}
              alignItems="center"
            >
              <label>{t('Photo (optional)')}</label>
              {photo ?
                <div className='photo-prev-wrapper'>
                  <IconButton
                    className='close-button'
                    aria-label="close"
                    onClick={() => setPhoto('')}
                  >
                    <CloseIcon />
                  </IconButton>
                  <img src={photo} alt={t("user")} />
                </div>
                :
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setOpenPhotoDialog(true)}
                  sx={{ borderRadius: '8px', border: '1px solid' }}
                >
                  <Stack
                    direction="row" gap={1} >
                    <LocalSeeOutlinedIcon />
                    {open && t('Take photo')}
                  </Stack>
                </Button>
              }
            </Stack>
          }
          {!isOnline && open && (enabled_visit_photo || save_identification_photo) &&
            <Alert className='Field' severity="info">
              {t('offline_photos_disabled')}
            </Alert>
          }
          {ask_visit_license_plate &&
            <Field
              label={t('license_plate')}
              value={license_plate}
              onChange={onChangeLicensePlate}
              onBlur={() => evalLicensePlate()}
              error={!!licensePlateError}
              helperText={licensePlateError}
              direction={open ? 'row' : 'column'}
            />
          }
          <Field
            direction='column'
            minRows={4}
            alignItems='left'
            multiline
            label={` ${t('Notes')} ${t('(optional)')}`}
            value={notes}
            placeholder={t("If you wish, you can write a note.")}
            onChange={e => setNotes(e.target.value)}
          />
          <Stack direction="row" gap={2} className={`buttons-wrapper  ${open ? '' : 'hide'} `}>
            <Button
              disabled={!isEnableSave()}
              variant="contained"
              fullWidth
              size="large"
              onClick={onSave}
            >
              {t('confirm')}
            </Button>
          </Stack>
        </Stack>
      </div>
      <IconButton
        variant="outlined"
        onClick={toggleDrawer}
        className={`button-drawer-toggle ${open ? 'open' : ''}`}
      >
        {open ? <ChevronLeft /> : <ChevronRight />}
      </IconButton>
      <PhotoDialog
        open={openPhotoDialog}
        setOpen={setOpenPhotoDialog}
        onClose={onClosePhotoDialog}
        setPhoto={setPhoto}
        photo={photo}
      />
      <BlackListDialog
        open={blackListDialog}
        onClose={onCloseblackListDialog}
        user={blackListUser}
        rejectReason={rejectReason}
      />
      <QRDialog
        open={openQRDialog}
        onClose={onCloseQRDialog}
        onChangeUniqueIdentificator={onChangeUniqueIdentificator}
        onInvalidQR={onInvalidQR}
      />
      <MRZDialog
        open={openMRZDialog}
        onClose={() => setOpenMRZDialog(false)}
        onChangDataByID={onChangDataByID}
      />
      <IDDialog
        open={openIDDialog}
        onClose={() => setOpenIDDialog(false)}
        setPhoto={setIdentificationPhoto}
        onChangDataByID={onChangDataByID}
        photo={identificationPhoto}
        enabled_OCR_extraction={enabled_identification_photo}
        enabled_local_ocr={enabled_local_ocr}
      />
      <InvitationNotesDialog
        open={openInvitationNotesDialog}
        onClose={() => setOpenInvitationNotesDialog(false)}
        setNoteIsAlreadyRead={setNoteIsAlreadyRead}
        notes={invitationNotes}
        hostName={hostName}
      />
      <CheckoutDialog
        open={checkout}
        onClose={onCloseCheckoutDialog}
        onExitCheckoutDialog={onExitCheckoutDialog}
        onEditCheckoutDialog={onEditCheckoutDialog}
      />
      <Dialog
        open={qrResultModal.open}
        onClose={closeQRResultModal}
        maxWidth="xs"
        fullWidth
        className='qr-result-modal'
      >
        <DialogContent>
          <Stack alignItems="center" className={`qr-result-modal-content ${qrResultModal.variant}`}>
            <IconButton
              aria-label="close"
              onClick={closeQRResultModal}
              className='qr-result-modal-close'
              color='primary'
            >
              <CloseIcon />
            </IconButton>
            {qrResultModal.variant === 'info'
              ? <InfoOutlinedIcon
                  className='qr-result-modal-icon'
                  sx={{
                    width: '60px',
                    height: '60px',
                    fill: '#1D8099'
                  }}
                />
              : <HighlightOffOutlinedIcon
                  className='qr-result-modal-icon'
                  width={60}
                  height={60}
                />
            }
            <h2>{qrResultModal.title}</h2>
            <p
              style={
                { marginBottom: qrResultModal.details.length ? '24px' : '' }
              }
            >
              {qrResultModal.description}
            </p>
            {qrResultModal.details.map((detail, index) => !!detail ? (
              <p
                key={`${detail}-${index}`}
                className={index === 0 ? 'qr-result-modal-detail-title' : 'qr-result-modal-detail-line'}
              >
                {detail}
              </p>
            ) : null)}
            {qrResultModal.schedule && (
              <Stack className='qr-result-modal-schedule'>
                <Stack direction="row" gap={1.5} alignItems="flex-start">
                  <EventOutlinedIcon className='qr-result-modal-schedule-icon' />
                  <Stack alignItems="flex-start" gap={0.5}>
                    <span className='qr-result-modal-schedule-label'>{t('date')}</span>
                    <span className='qr-result-modal-schedule-value'>{qrResultModal.schedule.date}</span>
                  </Stack>
                </Stack>
                <Stack direction="row" gap={1.5} alignItems="flex-start">
                  <AccessTimeOutlinedIcon className='qr-result-modal-schedule-icon' />
                  <Stack alignItems="flex-start" gap={0.5}>
                    <span className='qr-result-modal-schedule-label'>{t('Visit schedule')}</span>
                    <span className='qr-result-modal-schedule-value'>{qrResultModal.schedule.time}</span>
                  </Stack>
                </Stack>
              </Stack>
            )}
            {qrResultModal.location && (
              <Stack className='qr-result-modal-schedule qr-result-modal-location'>
                <Stack direction="row" gap={2} alignItems="flex-start">
                  <Building className='qr-result-modal-schedule-icon' width={24} height={24} />
                  <Stack alignItems="flex-start" gap={0.5}>
                    <span className='qr-result-modal-schedule-label'>{t('building')}</span>
                    <span className='qr-result-modal-schedule-value'>{qrResultModal.location.building}</span>
                  </Stack>
                </Stack>
                <Stack direction="row" gap={2} alignItems="flex-start">
                  <LayersOutlinedIcon className='qr-result-modal-schedule-icon' />
                  <Stack alignItems="flex-start" gap={0.5}>
                    <span className='qr-result-modal-schedule-label'>{t('Destination')}</span>
                    <span className='qr-result-modal-schedule-value'>{qrResultModal.location.destination}</span>
                  </Stack>
                </Stack>
              </Stack>
            )}
            <Button
              fullWidth
              size="large"
              variant="contained"
              onClick={onQRResultModalAction}
            >
              {qrResultModal.buttonLabel}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </div>
  )
}

const mapStateToProps = state => {
  return {
    isOnline: state.utilities.isOnline,
    loading: state.backdrop.loading,
    enabledInfraRedReader: state.setting.enabledInfraRedReader,
    enabledMrzWebcam: state.setting.enabledMrzWebcam,
    visitTypes: state.profile.visit_types,
    isCostaRica: !!state.profile.enabled_costa_rica,
    isAFZ: state.profile.domain === 'afz',
    visitDestinations: state.profile.visitDestinations,
    enabled_visit_photo: state.profile.enabled_visit_photo,
    enabled_visit_types: state.profile.enabled_visit_types,
    enabled_visit_host: state.profile.enabled_visit_host,
    enabled_visit_qr: state.profile.enabled_visit_qr,
    enabled_identification_photo: state.profile.enabled_identification_photo,
    save_identification_photo: state.profile.save_identification_photo,
    enabled_local_ocr: state.profile.enabled_local_ocr,
    enabled_black_list_by_name: state.profile.enabled_black_list_by_name,
    ask_visit_identification: state.profile.ask_visit_identification,
    ask_visit_comes_from: state.profile.ask_visit_comes_from,
    ask_visit_laptop: state.profile.ask_visit_laptop,
    ask_visit_card_number: state.profile.ask_visit_card_number,
    ask_visit_license_plate: state.profile.ask_visit_license_plate,
    custom_visit_identification_label: state.profile.custom_visit_identification_label,
    building_id: state.profile.building_id,
    companies: state.profile.companies,
    buildings: state.profile.buildings
  }
}

const mapDispatchToProps = {
  setSnackbar,
  setLoading,
  defaultSuccess,
  defaultCatch
}

export default connect(mapStateToProps, mapDispatchToProps)(VisitForm)
